import { z } from "zod";

/**
 * The event form — **one** of it, for the three places that ask for the same seven things.
 *
 * The two design packages of 2026-10-07 say it in the same sentence: the Evento screen has the
 * *"mesmos campos, rótulos, dicas e validação de Minha Lista · Editar apresentação no Painel do
 * Anfitrião"*. Until now there were three implementations of that sentence —
 * `lib/create-list` (the host's wizard), `lib/host-panel/event-details` (the panel) and
 * `lib/admin/nova-lista` (the store) — with three copies of the same labels, the same rules and
 * the same error strings, already diverged: the wizard asked for name and surname in one field
 * and had no welcome message at all.
 *
 * This module is the single source. It was **seeded from the internal flow**, which built the
 * V2 screen first and in full (`NOVA_LISTA_COPY.event`, `internalEventSchema`,
 * `assistedListName`): every string here is that one, moved, not reworded. The internal flow
 * keeps importing the same names from its own modules, which now re-export from here — so its
 * behaviour is identical and its tests did not have to change.
 *
 * What lives here is what the three share. What belongs to one of them stays there: the store's
 * location and administrator pickers, the panel's cover and its per-block saving, the wizard's
 * suggestion slug.
 */

/** A name part of the event: at least two characters after trim (Req. 8.5). */
export const MIN_EVENT_NAME_LENGTH = 2;

/** `gift_lists.event_location` is `VARCHAR(255)`; the field is what fills it. */
export const EVENT_LOCATION_MAX = 255;

/** The welcome message's ceiling, as the panel's counter has always announced it. */
export const WELCOME_MESSAGE_MAX = 240;

/**
 * The guest count: "maior que zero, até 5 dígitos" (both packages, §5.5).
 *
 * Sent to the backend since 2026-10-09, when the column was exposed on the DTOs. It used to be
 * collected and dropped, because the API refuses a key it does not know — see
 * {@link BACKEND_ACCEPTS_GUEST_COUNT} for the whole story.
 */
export const GUEST_COUNT_MIN = 1;
export const GUEST_COUNT_MAX = 99_999;

/**
 * Whether the backend accepts the guest count — **`true` since 2026-10-09**.
 *
 * It was `false` from 2026-10-07, when the column `gift_lists.guest_count` existed and no DTO
 * exposed it: with `forbidNonWhitelisted`, sending the field answered **400**, so what the host
 * typed was dropped by the two adapters that read this flag. Showing the field anyway was a
 * ruling (2026-10-08) — it kept the gap a backend pendency instead of looking like the front had
 * forgotten to build it.
 *
 * The backend exposed it on `Create`, `Update` (nullable, to clear) and `GiftListOutput`, and the
 * integration was this constant, exactly as the design promised. It stays as a named constant
 * rather than being inlined: it is the record of what the two adapters are doing and why, and
 * one line to flip if the contract ever moves again.
 */
export const BACKEND_ACCEPTS_GUEST_COUNT = true;

/**
 * The words, as the design writes them.
 *
 * Moved from `NOVA_LISTA_COPY.event` without a change, and the eyebrow, title, lede and submit
 * stay out: those are the only things the three screens legitimately say differently — the store
 * speaks of "o cliente", the host of "você", the panel introduces a tab and not a step.
 */
export const EVENT_FORM_COPY = {
  labels: {
    recipientFirstName: "Seu nome",
    recipientLastName: "Seu sobrenome",
    otherPartyFirstName: "Nome do seu par",
    otherPartyLastName: "Sobrenome do seu par",
    listName: "Nome da lista",
    welcomeMessage: "Mensagem de boas-vindas",
    eventDate: "Data do evento",
    guestCount: "Número de convidados",
    eventLocation: "Local do evento",
  },
  placeholders: {
    firstName: "Primeiro nome",
    lastName: "O restante do nome",
    /** Shown in the read-only "Nome da lista" while both parties are empty. */
    listName: "Preenchido a partir dos participantes",
    guestCount: "Quantas pessoas",
    eventLocation: "Cidade, ou o nome do lugar",
  },
  help: {
    listName: "Criado a partir dos nomes do casal. Para alterar, edite os campos anteriores.",
    welcomeMessage: "As primeiras palavras que seus convidados vão ler ao abrir a lista.",
    eventLocation: "Escreva como está no convite: cidade, ou o nome do lugar.",
  },
  errors: {
    recipientFirstName: "Informe o nome de quem recebe.",
    recipientLastName: "Informe o sobrenome de quem recebe.",
    otherPartyFirstName: "Informe o nome da outra parte.",
    otherPartyLastName: "Informe o sobrenome da outra parte.",
    welcomeMessage: "A mensagem de boas-vindas não pode ficar vazia.",
    welcomeMessageTooLong: `A mensagem de boas-vindas passa de ${WELCOME_MESSAGE_MAX} caracteres.`,
    eventDate: "A data do evento não pode ficar vazia.",
    /** The floor for a date being chosen, the same words wherever a date is chosen. */
    eventDateInPast: "O evento precisa ser a partir de amanhã.",
    guestCount: "Informe quantos convidados são esperados.",
    eventLocation: "O local do evento não pode ficar vazio.",
    /**
     * Its own message, and not `eventLocation` reused. The city field this replaced used one
     * string for both borders, so a field the host had just overfilled was told it was empty.
     */
    eventLocationTooLong: `O local do evento é longo demais: use até ${EVENT_LOCATION_MAX} caracteres.`,
  },
} as const;

/** A name part: trimmed, at least two characters. */
export function eventNameField(message: string) {
  return z.string(message).trim().min(MIN_EVENT_NAME_LENGTH, message);
}

/** Required free text, trimmed, with its own word for "too long". */
export function requiredTextField(message: string, max: number, tooLong: string) {
  return z
    .string(message)
    .transform((value) => value.trim())
    .pipe(z.string().min(1, message).max(max, tooLong));
}

/** The place, as the host writes it on the invitation. */
export function eventLocationField() {
  return requiredTextField(
    EVENT_FORM_COPY.errors.eventLocation,
    EVENT_LOCATION_MAX,
    EVENT_FORM_COPY.errors.eventLocationTooLong,
  );
}

/** The welcome message: the first thing a guest reads. */
export function welcomeMessageField() {
  return requiredTextField(
    EVENT_FORM_COPY.errors.welcomeMessage,
    WELCOME_MESSAGE_MAX,
    EVENT_FORM_COPY.errors.welcomeMessageTooLong,
  );
}

/**
 * How many guests are expected.
 *
 * Taken as text because that is what an input gives, and because an empty field has to report
 * "informe" and not a type error. Greater than zero and at most five digits, per the design.
 */
export function guestCountField() {
  return z
    .string(EVENT_FORM_COPY.errors.guestCount)
    .transform((value) => value.trim())
    .pipe(
      z
        .string()
        .min(1, EVENT_FORM_COPY.errors.guestCount)
        .regex(/^\d+$/, EVENT_FORM_COPY.errors.guestCount),
    )
    .transform((value) => Number(value))
    .pipe(
      z
        .number()
        .int(EVENT_FORM_COPY.errors.guestCount)
        .min(GUEST_COUNT_MIN, EVENT_FORM_COPY.errors.guestCount)
        .max(GUEST_COUNT_MAX, EVENT_FORM_COPY.errors.guestCount),
    );
}

/**
 * The guest count where the screen **re-opens on saved data** — the panel's Dados do Evento.
 *
 * Optional there, and required in the creation, for one reason: the backend hands no guest count
 * back (a pendency with the backend team), so the field re-opens empty every time. Required
 * would mean the host could not save a corrected surname without retyping a number that never
 * sticks — a field that cannot remember must not hold the block hostage.
 *
 * The creation asks once and can require it; the panel asks again and cannot. The day the DTO
 * answers, this collapses into `guestCountField`.
 */
export function savedGuestCountField() {
  return z.union([z.literal(""), guestCountField()]);
}

/*
 * ─── The same count, in the request body ──────────────────────────────────────────────────────
 *
 * **A form field and a request field are different positions, and this is where that bit.**
 *
 * The two fields above read what an `<input>` gives — text — and hand back a number. Every
 * screen posts its **parsed** draft (`handleSubmit` gives the schema's output), so what reaches
 * the BFF is that number. The route schemas used to reuse the form fields, so they demanded text
 * and refused the number the screen had just produced: the creation's event step answered 400
 * on every submit, and the panel's block did the same whenever the host actually filled the
 * count — which is why it hid, since the field re-opens empty and `""` passed.
 *
 * Measured on the VPS on 2026-10-08, and the reason it got this far is that nothing crossed the
 * seam: the route tests posted text, the screen tests mocked the request, and my own curl
 * validation posted text too. The tests below (`event-step-wire.test.ts`) now feed one into the
 * other, which is the only shape of test that could have caught it.
 */

/** The count as the wire carries it: the number the form produced, under the same bounds. */
export function guestCountValue() {
  return z
    .number(EVENT_FORM_COPY.errors.guestCount)
    .int(EVENT_FORM_COPY.errors.guestCount)
    .min(GUEST_COUNT_MIN, EVENT_FORM_COPY.errors.guestCount)
    .max(GUEST_COUNT_MAX, EVENT_FORM_COPY.errors.guestCount);
}

/** The optional count as the wire carries it: the number, or the empty field left untouched. */
export function savedGuestCountValue() {
  return z.union([z.literal(""), guestCountValue()]);
}

/** The four name parts the list's name is derived from. */
export type EventNameParts = {
  readonly recipientFirstName: string;
  readonly recipientLastName: string;
  readonly otherPartyFirstName: string;
  readonly otherPartyLastName: string;
};

/** "Maria" + "Helena Almeida" → "Maria Helena Almeida"; an empty part drops its space. */
export function joinFullName(firstName: string, lastName: string): string {
  return [firstName.trim(), lastName.trim()].filter((part) => part !== "").join(" ");
}

/**
 * The list's name: `"{nome sobrenome} & {nome sobrenome}"`.
 *
 * **Name and surname of both parties**, which is what the field's own help text promises. While
 * only one party is filled it is that party's name, and with neither it is empty, so the
 * read-only field shows its placeholder.
 *
 * Not to be confused with how the list is **displayed**: the hero and the page title use the
 * first-name fields alone ("Maria Helena & João", `coupleTitle`), which is what the prototype
 * does. Input and display are different rules, and both are deliberate.
 */
export function listNameFrom(parts: EventNameParts): string {
  const recipient = joinFullName(parts.recipientFirstName, parts.recipientLastName);
  const otherParty = joinFullName(parts.otherPartyFirstName, parts.otherPartyLastName);

  if (recipient !== "" && otherParty !== "") return `${recipient} & ${otherParty}`;

  return recipient || otherParty;
}

/** The four name parts, as every screen validates them. */
export function eventNameFieldsSchema() {
  return {
    recipientFirstName: eventNameField(EVENT_FORM_COPY.errors.recipientFirstName),
    recipientLastName: eventNameField(EVENT_FORM_COPY.errors.recipientLastName),
    otherPartyFirstName: eventNameField(EVENT_FORM_COPY.errors.otherPartyFirstName),
    otherPartyLastName: eventNameField(EVENT_FORM_COPY.errors.otherPartyLastName),
  };
}

/** "Maria Helena Almeida" → first "Maria", surname "Helena Almeida": the first word is the name. */
export function splitFullName(fullName: string | null | undefined): {
  firstName: string;
  lastName: string;
} {
  const words = (fullName ?? "").trim().split(/\s+/).filter(Boolean);

  return { firstName: words[0] ?? "", lastName: words.slice(1).join(" ") };
}
